import { useEffect, useState } from "react";

import { useTheme } from "@emotion/react";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { ListingCartProvider } from "@hl/shared-features/lib/features/cart/ListingCartProvider";
import { QuicksellCartProvider } from "@hl/shared-features/lib/features/cart/QuickSellProvider";
import { Button, Flex, Loader, Tabs, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Link, useNavigate, useParams } from "react-router-dom";

import { TOOLS_URL, USER_URL } from "../../config";
import { Collected } from "../marketplace-management/Collected";
import { Listings } from "../marketplace-management/Listings";
import { ReceivedOffers } from "../marketplace-management/ReceivedOffers";
import { SentOffers } from "../marketplace-management/SentOffers";

import UserCollections from "./UserCollections";
import { UserWatchlistTab } from "./followers-and-watchlist/UserWatchlistTab";

export type OnLoadProps = {
  onLoad?: ({
    totalCount,
    hasError,
  }: {
    totalCount?: number;
    hasError?: boolean;
  }) => void;
  isOwner?: boolean;
  active?: boolean;
  slug?: string;
};

type PartialState = {
  totalCount?: number;
  loading: boolean;
};

export enum UserProfileTabs {
  Created = "created",
  Collected = "collected",
  Listings = "listings",
  SentOffers = "sent-offers",
  ReceivedOffers = "received-offers",
  CreatorRewards = "creator-rewards",
  Watchlist = "watchlist",
}

const PUBLIC_TABS: UserProfileTabs[] = [
  UserProfileTabs.Collected,
  UserProfileTabs.Created,
];

const UserTabs = ({
  slug,
  slugAddress,
  isOwner,
}: {
  slug: string; // Used for the URL
  slugAddress: string; // Actual resolved 0x address
  isOwner: boolean;
}) => {
  const { loading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);
  const navigate = useNavigate();
  const params = useParams();
  const view = params.view as UserProfileTabs;
  const [activeTab, setActiveTab] = useState<UserProfileTabs | null>(null);
  const [createdState, setCreatedState] = useState<PartialState>({
    totalCount: 0,
    loading: true,
  });

  const enableFollows = useFeatureFlags(FEATURE_FLAGS.ENABLE_FOLLOWS);

  const hasCreatedCollections =
    createdState.totalCount != null &&
    createdState.totalCount > 0 &&
    !createdState.loading;

  useEffect(() => {
    const initialTab = hasCreatedCollections
      ? UserProfileTabs.Created
      : UserProfileTabs.Collected;

    if (view == null && activeTab == null) {
      return setActiveTab(createdState.loading ? null : initialTab);
    }

    if (view && Object.values(UserProfileTabs).includes(view)) {
      if (PUBLIC_TABS.includes(view)) {
        return setActiveTab(view);
      }

      if (loading) {
        return;
      }

      if (isOwner) {
        return setActiveTab(view);
      }

      return changeTab(initialTab);
    }
  }, [view, loading, isOwner, hasCreatedCollections, createdState.loading]);

  const changeTab = (tab: string) =>
    navigate(
      `${USER_URL.user.marketplace.default.replace(":slug", slug ?? "")}/${tab}`
    );

  return (
    <Tabs
      value={activeTab}
      onTabChange={changeTab}
      styles={{
        tab: {
          fontWeight: WEIGHT_BOLD,
        },
        tabsList: {
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap-reverse",
        },
        tabLabel: {
          display: "flex",
          alignItems: "center",
          whiteSpace: "pre",
          height: 20,
        },
      }}
    >
      <Tabs.List mb={isMobile ? 0 : 24}>
        <Flex align="center" columnGap={32} wrap="wrap">
          {hasCreatedCollections && (
            <Tabs.Tab value={UserProfileTabs.Created} py={13}>
              Created <TabCount state={createdState} />
            </Tabs.Tab>
          )}
          <Tabs.Tab value={UserProfileTabs.Collected} py={13}>
            Collected
          </Tabs.Tab>
          {isOwner && (
            <>
              <Tabs.Tab value={UserProfileTabs.Listings} py={13}>
                Listings
              </Tabs.Tab>
              <Tabs.Tab value={UserProfileTabs.ReceivedOffers} py={13}>
                Offers received
              </Tabs.Tab>
              <Tabs.Tab value={UserProfileTabs.SentOffers} py={13}>
                Offers sent
              </Tabs.Tab>
              {enableFollows && (
                <Tabs.Tab value={UserProfileTabs.Watchlist} py={13}>
                  Watchlist
                </Tabs.Tab>
              )}
            </>
          )}
        </Flex>
        {isOwner && createdState.totalCount === 0 && !createdState.loading && (
          <Button px="sm" component={Link} to={TOOLS_URL.base}>
            Create collection
          </Button>
        )}
      </Tabs.List>

      <Tabs.Panel value={UserProfileTabs.Created} mt={16}>
        <UserCollections
          active={activeTab === UserProfileTabs.Created}
          slug={slugAddress}
          isOwner={isOwner}
          onLoad={(totalCount) =>
            setCreatedState({ totalCount, loading: false })
          }
        />
      </Tabs.Panel>
      <Tabs.Panel value={UserProfileTabs.Collected}>
        <ListingCartProvider>
          <Collected
            isOwner={isOwner}
            active={activeTab === UserProfileTabs.Collected}
            slug={slugAddress}
          />
        </ListingCartProvider>
      </Tabs.Panel>
      {isOwner && (
        <>
          <Tabs.Panel value={UserProfileTabs.Listings}>
            {activeTab === UserProfileTabs.Listings && <Listings />}
          </Tabs.Panel>
          <Tabs.Panel value={UserProfileTabs.ReceivedOffers}>
            {activeTab === UserProfileTabs.ReceivedOffers && (
              <QuicksellCartProvider>
                <ReceivedOffers />
              </QuicksellCartProvider>
            )}
          </Tabs.Panel>
          <Tabs.Panel value={UserProfileTabs.SentOffers}>
            {activeTab === UserProfileTabs.SentOffers && <SentOffers />}
          </Tabs.Panel>
          {enableFollows && (
            <Tabs.Panel value={UserProfileTabs.Watchlist}>
              {activeTab === UserProfileTabs.Watchlist && <UserWatchlistTab />}
            </Tabs.Panel>
          )}
        </>
      )}
    </Tabs>
  );
};

export default UserTabs;

const TabCount = ({ state }: { state: PartialState }) => {
  if (!state.loading && state.totalCount == undefined) {
    return null;
  }

  return state.loading ? (
    <Loader size={14} />
  ) : (
    <Text
      fw={WEIGHT_BOLD}
      color={TEXT_COLOR.PLACEHOLDER}
      display="flex"
      bg={SECONDARY_COLOR}
      fs="xs"
      h={20}
      px={8}
      sx={{ borderRadius: 999, alignItems: "center" }}
      size="xs"
    >
      {state.totalCount ?? 0}
    </Text>
  );
};
